import { Icon, Intent, Text } from "@blueprintjs/core";
import {
  GithubRefType,
  OrgId,
  SharedAssetRole,
  SyncRepositoryId,
  VcsConnectionId,
  VcsConnectionType,
  VcsPackageId,
  assertNever,
  groupByDiscriminate,
  isSharedAssetRoleSuperset,
  vcsTypeToDisplayName,
} from "@hex/common";
import React, { useCallback, useMemo, useState } from "react";
import styled, { css } from "styled-components";

import {
  HexButton,
  HexMenu,
  HexMenuItem,
  HexPopover,
  HexTooltip,
} from "../../hex-components";
import { SharedAssetPermissionSummary } from "../common/SharedAssetPermissionSummary";
import {
  HeaderCell,
  TableCell,
  TableList,
  TableListProps,
} from "../common/table/TableList";
import {
  FeatureGateDisabledOverlay,
  FeatureGateZeroState,
} from "../feature-gate/FeatureGateZeroState";
import {
  AllProjectsIcon,
  CodeCellIcon,
  GitBranchIcon,
  GitHubIcon,
  GitLabIcon,
  LockIcon,
  MoreMenuIcon,
} from "../icons/CustomIcons";

import { AddRepoDialog } from "./AddRepoDialog";
import {
  DefaultIntrinsicGroupFragment,
  SyncRepoFragment,
  VcsPackageFragment,
} from "./queries.generated";
import { REF_TYPE_ICON_MAP } from "./RefSelector";

export interface OrgRepoListProps {
  connectionType: VcsConnectionType;
  repos: readonly SyncRepoFragment[] | readonly VcsPackageFragment[];
  intrinsicGroups: readonly DefaultIntrinsicGroupFragment[];
  featureGated: boolean;
  orgId: OrgId;
  setPackageToDelete?: (packageId: VcsPackageId) => void;
  setRepoToDelete?: (repoId: SyncRepositoryId) => void;
  noGithubConnection: boolean;
  vcsConnectionId: VcsConnectionId;
}

const StyledTableList = styled<
  React.ComponentType<TableListProps<VcsPackageFragment | SyncRepoFragment>>
>(TableList)<{ $minHeight: number }>`
  ${({ $minHeight }) => `min-height: ${$minHeight}px`}
`;

export type SyncRepoAndWorkingBranch = Omit<SyncRepoFragment, "__typename"> & {
  workingBranch: string;
  __typename: "SyncRepoAndWorkingBranch";
};

const PackageContainer = styled.div`
  display: flex;
  width: fit-content;
  max-width: 100%;
  height: 24px;
  margin-right: 6px;

  border: 1px solid ${({ theme }) => theme.borderColor.DEFAULT};
  border-radius: ${({ theme }) => theme.borderRadius};

  pointer-events: all;
`;

const RepoOuterContainer = styled.a`
  display: flex;
  min-width: 0;

  color: ${({ theme }) => theme.fontColor.DEFAULT};

  border-right: 1px solid ${({ theme }) => theme.borderColor.DEFAULT};

  &:hover {
    color: ${({ theme }) => theme.fontColor.DEFAULT};
    text-decoration: none;
  }
`;

const RepoContainer = styled.div`
  display: flex;

  gap: 4px;
  align-items: center;
  min-width: 0;
  height: 100%;
  padding: 0 7px 0 5px;

  background-color: ${({ theme }) => theme.backgroundColor.MUTED};

  &:hover {
    background-color: ${({ theme }) => theme.hoverColor};

    transition: background-color ${({ theme }) => theme.animation.duration}
      ${({ theme }) => theme.animation.easing};
  }
`;

const RefOuterContainer = styled.a`
  display: flex;
  min-width: 0;

  color: ${({ theme }) => theme.fontColor.MUTED};

  &:hover {
    color: ${({ theme }) => theme.fontColor.MUTED};
    text-decoration: none;
  }
`;

const RefContainer = styled.div`
  display: flex;

  gap: 4px;
  align-items: center;
  min-width: 0;
  height: 100%;
  padding: 0 7px;

  &:hover {
    background-color: ${({ theme }) => theme.hoverColor};

    transition: background-color ${({ theme }) => theme.animation.duration}
      ${({ theme }) => theme.animation.easing};
  }
`;

const Wrapper = styled.div`
  width: 100%;
`;

const PackageRow = styled.div`
  font-size: ${({ theme }) => theme.fontSize.SMALL};
`;

const CodeOuterContainer = styled.div`
  border-left: 1px solid ${({ theme }) => theme.borderColor.DEFAULT};
`;

const CodeContainer = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0 4px;
`;

const InstallCommand = styled.div`
  font-size: ${({ theme }) => theme.fontSize.EXTRA_SMALL};
  font-family: ${({ theme }) => theme.fontFamily.MONO};
`;

const DescriptionContainer = styled.div`
  margin-top: 6px;

  color: ${({ theme }) => theme.fontColor.MUTED};
  white-space: normal;
`;

const LockTooltip = styled(HexTooltip)`
  display: flex !important;
  align-items: center;
  height: 100%;
`;

export function generateRefLink({
  connectionType,
  repo,
}: {
  connectionType: VcsConnectionType;
  repo: VcsPackageFragment | SyncRepoFragment | SyncRepoAndWorkingBranch;
}): string {
  let baseString;
  switch (connectionType) {
    case VcsConnectionType.GITLAB_SYNC:
      baseString = `https://gitlab.com/${repo.repoOwner}/${repo.repoName}/`;
      break;
    case VcsConnectionType.GITHUB:
    case VcsConnectionType.GITHUB_SYNC:
      baseString = `https://github.com/${repo.repoOwner}/${repo.repoName}/`;
      break;
    default:
      assertNever(connectionType, connectionType);
  }

  if (repo.__typename === "VcsPackage") {
    const refType = repo.refType;
    switch (refType) {
      case GithubRefType.BRANCH:
        return `${baseString}tree/${repo.refName}`;
      case GithubRefType.TAG:
        return `${baseString}releases/tag/${repo.refName}`;
      case GithubRefType.COMMIT:
        return `${baseString}commit/${repo.refName}`;
      default:
        assertNever(refType, refType);
    }
  } else if (repo.__typename === "SyncRepository") {
    return `${baseString}tree/${repo.mainBranch}`;
  } else if (repo.__typename === "SyncRepoAndWorkingBranch") {
    return `${baseString}tree/${repo.workingBranch}`;
  } else {
    assertNever(repo, repo);
  }
}

export function renderRepo({
  connectionType,
  repo,
}: {
  connectionType: VcsConnectionType;
  repo: VcsPackageFragment | SyncRepoFragment | SyncRepoAndWorkingBranch;
}): React.ReactElement {
  let refName = "";
  if (repo.__typename === "VcsPackage") {
    refName = repo.refName;
  } else if (repo.__typename === "SyncRepository") {
    refName = repo.mainBranch;
  } else if (repo.__typename === "SyncRepoAndWorkingBranch") {
    refName = repo.workingBranch;
  } else {
    assertNever(repo, repo);
  }

  let repoLink = "";
  switch (connectionType) {
    case VcsConnectionType.GITLAB_SYNC:
      repoLink = `https://gitlab.com/${repo.repoOwner}/${repo.repoName}`;
      break;
    case VcsConnectionType.GITHUB:
    case VcsConnectionType.GITHUB_SYNC:
      repoLink = `https://github.com/${repo.repoOwner}/${repo.repoName}`;
      break;
    default:
      assertNever(connectionType, connectionType);
  }

  return (
    <PackageRow>
      <PackageContainer>
        <RepoOuterContainer
          href={repoLink}
          rel="noopener noreferrer"
          target="_blank"
        >
          <RepoContainer>
            {connectionType === VcsConnectionType.GITLAB_SYNC ? (
              <GitLabIcon />
            ) : (
              <GitHubIcon />
            )}
            <Text ellipsize={true} title={`${repo.repoOwner}/${repo.repoName}`}>
              {repo.repoName}
            </Text>
          </RepoContainer>
        </RepoOuterContainer>
        <RefOuterContainer
          href={generateRefLink({ connectionType, repo })}
          rel="noopener noreferrer"
          target="_blank"
        >
          <RefContainer>
            <Icon
              icon={
                repo.__typename === "VcsPackage" ? (
                  REF_TYPE_ICON_MAP[repo.refType]
                ) : (
                  <GitBranchIcon />
                )
              }
            />
            <Text ellipsize={true}>{refName}</Text>
            {(repo.__typename === "SyncRepository" ||
              repo.__typename === "SyncRepoAndWorkingBranch") &&
              repo.requirePullRequests && (
                <LockTooltip content="Requires pull request to merge to main branch ">
                  <LockIcon
                    css={`
                      margin-left: 4px;
                    `}
                  />
                </LockTooltip>
              )}
          </RefContainer>
        </RefOuterContainer>
        {repo.__typename === "VcsPackage" && repo.installCommand && (
          <CodeOuterContainer>
            <HexTooltip
              content={
                <>
                  Install command:{" "}
                  <InstallCommand>{repo.installCommand}</InstallCommand>
                </>
              }
              css={`
                height: 100%;
              `}
              placement="bottom"
            >
              <CodeContainer>
                <CodeCellIcon iconSize={12} />
              </CodeContainer>
            </HexTooltip>
          </CodeOuterContainer>
        )}
      </PackageContainer>
      {repo.description && (
        <DescriptionContainer>{repo.description}</DescriptionContainer>
      )}
    </PackageRow>
  );
}

export function renderVcsPackageTableCells({
  closeRepoMenu,
  connectionType,
  isOpen,
  menuItems,
  openRepoMenu,
  repo,
  showPermissions,
}: {
  connectionType: VcsConnectionType;
  repo: VcsPackageFragment | SyncRepoFragment | SyncRepoAndWorkingBranch;
  menuItems: React.ReactElement[];
  openRepoMenu: (repoId: VcsPackageId | SyncRepositoryId) => void;
  closeRepoMenu: () => void;
  isOpen: boolean;
  showPermissions?: boolean;
}): TableCell[] {
  const packageContent = renderRepo({ connectionType, repo });

  const cells = [
    {
      tag: "td" as const,
      css: css`
        padding: 10px 0;
      `,
      content: packageContent,
    },

    {
      tag: "td" as const,
      css: css`
        width: 24px;
      `,
      content: (
        <HexPopover
          content={<HexMenu>{menuItems}</HexMenu>}
          isOpen={isOpen}
          minimal={true}
          placement="bottom-end"
          onClose={closeRepoMenu}
        >
          <HexButton
            icon={<MoreMenuIcon />}
            minimal={true}
            small={true}
            // eslint-disable-next-line react/jsx-no-bind
            onClick={() => openRepoMenu(repo.id)}
          />
        </HexPopover>
      ),
    },
  ];
  if (showPermissions) {
    const importers = repo.grants
      .filter((p) =>
        isSharedAssetRoleSuperset(p.role, SharedAssetRole.IMPORTER),
      )
      .map((p) => p.holder);

    const groupedImporters = groupByDiscriminate(importers, "__typename");
    cells.splice(1, 0, {
      tag: "td" as const,
      css: css``,
      content: (
        <SharedAssetPermissionSummary
          groups={groupedImporters.Group ?? []}
          intrinsicGroups={groupedImporters.IntrinsicGroup ?? []}
        />
      ),
    });
  }
  return cells;
}

export const OrgRepoList: React.FunctionComponent<OrgRepoListProps> =
  React.memo(function OrgRepoList({
    connectionType,
    featureGated,
    intrinsicGroups,
    noGithubConnection,
    orgId,
    repos,
    setPackageToDelete,
    setRepoToDelete,
    vcsConnectionId,
  }) {
    const tableHeaders: HeaderCell[] = useMemo(
      () => [
        {
          content:
            connectionType === VcsConnectionType.GITHUB
              ? "Package"
              : "Repository",
        },
        { content: "Access" },
        { content: "" },
      ],
      [connectionType],
    );

    const emptyState = useMemo(
      () => ({
        title: `No ${
          connectionType === VcsConnectionType.GITHUB
            ? "packages"
            : "repositories"
        } yet`,
        $small: true,
        icon: <AllProjectsIcon />,
      }),
      [connectionType],
    );

    const [openMenuId, setOpenMenuId] = useState<string>();
    const [repoToEdit, setRepoToEdit] = useState<
      VcsPackageFragment | SyncRepoFragment | undefined
    >(undefined);
    const openRepoMenu = useCallback(
      (id) => setOpenMenuId(id),
      [setOpenMenuId],
    );
    const closeRepoMenu = useCallback(
      () => setOpenMenuId(undefined),
      [setOpenMenuId],
    );

    const openEditRepoDialog = useCallback(
      (repo: VcsPackageFragment | SyncRepoFragment) => setRepoToEdit(repo),
      [],
    );

    const closeEditPackageDialog = useCallback(
      () => setRepoToEdit(undefined),
      [],
    );

    const generateMenuItems = useCallback(
      (repo: VcsPackageFragment | SyncRepoFragment) => {
        return [
          <HexMenuItem
            key="editMenuItem"
            disabled={featureGated || noGithubConnection}
            text="Edit..."
            // eslint-disable-next-line react/jsx-no-bind
            onClick={() => openEditRepoDialog(repo)}
          />,
          <HexMenuItem
            key="deleteMenuItem"
            intent={Intent.DANGER}
            text="Delete..."
            // eslint-disable-next-line react/jsx-no-bind
            onClick={
              repo.__typename === "VcsPackage"
                ? () => setPackageToDelete?.(repo.id)
                : () => setRepoToDelete?.(repo.id)
            }
          />,
        ];
      },
      [
        noGithubConnection,
        featureGated,
        openEditRepoDialog,
        setPackageToDelete,
        setRepoToDelete,
      ],
    );

    const renderPackageCallback = useCallback(
      (repo: VcsPackageFragment | SyncRepoFragment): TableCell[] => {
        const menuItems = generateMenuItems(repo);
        return renderVcsPackageTableCells({
          connectionType,
          repo,
          menuItems,
          isOpen: openMenuId == repo.id,
          openRepoMenu,
          closeRepoMenu,
          showPermissions: true,
        });
      },
      [
        closeRepoMenu,
        connectionType,
        generateMenuItems,
        openMenuId,
        openRepoMenu,
      ],
    );

    return (
      <>
        {repoToEdit && (
          <AddRepoDialog
            connectionType={connectionType}
            defaultIntrinsicGroups={intrinsicGroups}
            isOpen={!!repoToEdit}
            orgId={orgId}
            repoToEdit={repoToEdit}
            vcsConnectionId={vcsConnectionId}
            onClose={closeEditPackageDialog}
          />
        )}
        <Wrapper>
          {featureGated && repos.length == 0 && (
            <FeatureGateZeroState
              css={`
                position: absolute;
                top: 0;
                left: 0;
                z-index: 10;
              `}
              description={`Upgrade your plan to ${
                connectionType === VcsConnectionType.GITHUB
                  ? "import GitHub packages."
                  : `sync your projects to ${vcsTypeToDisplayName(
                      connectionType,
                    )}.`
              }`}
            />
          )}
          <FeatureGateDisabledOverlay
            $disabled={featureGated && repos.length == 0}
          >
            <StyledTableList
              $minHeight={240}
              data={repos}
              dataToCells={renderPackageCallback}
              emptyState={emptyState}
              headers={tableHeaders}
            />
          </FeatureGateDisabledOverlay>
        </Wrapper>
      </>
    );
  });
